/* eslint-disable @typescript-eslint/no-explicit-any */
import { AbortController } from 'node-abort-controller'
import { logNewRelicEvent } from '../common/custom/newrelic-page-events'
import { getApiGatewayUrl } from './api-gateway'
import { ErrorNRLogs } from './error.tracking'
import { STATUS_CODE } from '@/root/constants/api-status-codes'
const isDev = process.env.NODE_ENV === 'development'

export const fetchRequest = async ({ url, config, timeout = 0 }) => {
  const apiGatewayUrl = getApiGatewayUrl(url)
  const controller = new AbortController()
  const { signal } = controller
  let timer
  if (timeout) {
    timer = setTimeout(() => {
      controller.abort()
      if (isDev) {
        console.info(`****ABORTED after ${timeout}ms*****`, {
          url: apiGatewayUrl,
          timer,
        })
      }
      clearTimeout(timer)
    }, timeout)
  }
  try {
    logNewRelicEvent(ErrorNRLogs.webFetchRequest, {
      url: apiGatewayUrl,
    })
    const resp = await fetch(apiGatewayUrl, {
      ...config,
      signal: timeout ? signal : null,
    })
    const response = await resp.json()
    if (resp.ok) {
      if (timeout) {
        clearTimeout(timer)
      }
      return response
    } else {
      if (timeout) {
        clearTimeout(timer)
      }
      const errorResp = { statusCode: resp.status }
      const errors = { ...response, ...errorResp }
      console.info(`GET API FAILED, ${apiGatewayUrl}`, errors)
      throw errors // eslint-disable-line
    }
  } catch (err: unknown) {
    if (timeout) {
      clearTimeout(timer)
    }
    logNewRelicEvent(ErrorNRLogs.webFetchError, {
      error: JSON.stringify(err),
      statusCode:
        'statusCode' in (err as object)
          ? (err as { statusCode?: number }).statusCode?.toString() ?? ''
          : '',
      url: apiGatewayUrl,
    })
    console.info(`GET FETCH FAILED, ${apiGatewayUrl}`, err)
    throw err // eslint-disable-line
  }
}

export const getFetchRequest = ({
  url = '',
  config,
  timeout = 3000,
  notHandleError = false,
}: {
  url: RequestInfo
  config?: RequestInit
  timeout?: number
  notHandleError?: boolean
}) => {
  const apiGatewayUrl = getApiGatewayUrl(url.toString())
  const controller =
    typeof window === 'undefined' ? undefined : new window.AbortController()
  const { signal } = controller ?? {}
  let timer
  if (timeout) {
    timer = setTimeout(() => {
      controller?.abort()
      if (isDev) {
        console.info(`****ABORTED after ${timeout}ms*****`, {
          url: apiGatewayUrl,
          timer,
        })
      }
      clearTimeout(timer)
    }, timeout)
  }
  logNewRelicEvent(ErrorNRLogs.webFetchRequest, {
    url: apiGatewayUrl,
  })
  return fetch(apiGatewayUrl, { ...config, signal })
    .then(async res => {
      const response = await res.json()
      if (res.ok) {
        if (timeout) {
          clearTimeout(timer)
        }
        return response
      } else {
        if (timeout) {
          clearTimeout(timer)
        }
        const err = {
          apiResponse: response,
          statusCode: res.status,
          url: res.url,
        }

        // Status 424 is for 2FA journey
        if (
          !notHandleError &&
          res.status === STATUS_CODE[424] &&
          typeof window === 'object' &&
          !window.location.pathname.includes('journey/2fa')
        ) {
          if (response?.data?.navlink?.web) {
            const url = new URL(response.data?.navlink.web)

            const search = new URLSearchParams(url.search)

            const currUrl = new URL(window.location.href)

            search.append(
              'return_to',
              encodeURIComponent(currUrl.pathname + currUrl.search).substring(
                0,
                1000,
              ),
            )

            window.location.href = `${url.pathname}?${search.toString()}`
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw err
      }
    })
    .catch(err => {
      logNewRelicEvent(ErrorNRLogs.webGetFetchError, {
        error: JSON.stringify(err),
        statusCode:
          'statusCode' in (err as object)
            ? (err as { statusCode?: number }).statusCode?.toString() ?? ''
            : '',
        url: apiGatewayUrl,
      })
      if (timeout) {
        clearTimeout(timer)
      }
      if (isDev) {
        console.error(
          `GET API FAILED, ${typeof url === 'string' ? apiGatewayUrl : ''}`,
          err,
        )
      }
      throw err
    })
}

// copied from frontend-webview
export const fetchPOSTRequest = async ({
  method = 'POST',
  url,
  headers,
  data,
  apiType,
}: {
  method?: string | undefined
  url: any
  headers: any
  data?: any
  apiType?: string
}) => {
  const apiGatewayUrl = getApiGatewayUrl(url)
  try {
    logNewRelicEvent(ErrorNRLogs.webFetchRequest, {
      url: apiGatewayUrl,
      apiType: apiType ?? '',
    })
    const resp = await fetch(apiGatewayUrl, {
      method,
      headers,
      ...(data && { body: JSON.stringify(data) }),
    })
    const response = await resp.json()
    if (resp.ok) return response
    else {
      const errorResp = { statusCode: resp.status }
      const errors = {
        // don't need an 'error' key because it's just an empty error
        error: new Error(),
        ...response,
        ...errorResp,
      }
      console.info(`${method} API FAILED, ${apiGatewayUrl}`, errors)
      // STATUS CODE 401 is for unauthorised user
      if (
        resp.status === STATUS_CODE[401] &&
        typeof window === 'object' &&
        window.location.pathname !== '/login'
      ) {
        // Temperory handling for logout page with 401 status code
        if (window.location.pathname.includes('logout')) {
          return response
        }
        window.location.href = '/logout'
      }
      throw errors // eslint-disable-line
    }
  } catch (err: unknown) {
    logNewRelicEvent(ErrorNRLogs.webFetchPostError, {
      error: JSON.stringify(err),
      statusCode:
        'statusCode' in (err as object)
          ? (err as { statusCode?: number }).statusCode?.toString() ?? ''
          : '',
      url: apiGatewayUrl,
      apiType: apiType ?? '',
    })
    if (isDev) {
      console.error(
        `GET API FAILED, ${typeof url === 'string' ? apiGatewayUrl : ''}`,
        err,
      )
    }
    throw err // eslint-disable-line
  }
}

export const fetchPostRequestWithFormData = async ({
  url,
  headers,
  formData,
}: {
  url: any
  headers: any
  formData: FormData
}) => {
  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    })
    const response = await resp.json()
    if (resp.ok) return response
    else {
      const errorResp = { statusCode: resp.status }
      const errors = {
        error: new Error(),
        ...response,
        ...errorResp,
      }
      console.info(`POST API FAILED, ${url}`, errors)
      // STATUS CODE 401 is for unauthorised user
      if (
        resp.status === STATUS_CODE[401] &&
        typeof window === 'object' &&
        window.location.pathname !== '/login'
      ) {
        window.location.href = '/logout?glitter=true'
      }
      throw errors
    }
  } catch (err: unknown) {
    logNewRelicEvent(ErrorNRLogs.webFetchPostWithFormDataError, {
      error: JSON.stringify(err),
      statusCode:
        'statusCode' in (err as object)
          ? (err as { statusCode?: number }).statusCode?.toString() ?? ''
          : '',
      url,
    })
    if (isDev) {
      console.error(
        `POST API FAILED, ${typeof url === 'string' ? url : ''}`,
        err,
      )
    }
    throw err
  }
}
